/**
 * Secondary — verlet chains that drive the rig's dynamic bones.
 *
 * The antenna, the shemagh tail, the drop-leg holster and the dump pouch are skinned to
 * bones that no animation layer touches. Each frame this solver reads where those bones
 * WOULD be at rest, integrates a small verlet chain with gravity and the inertia of the
 * attach point, then writes the bone rotations back. The result is lag and settle: gear
 * that arrives a beat after the body does. It costs a few dozen floats per character and
 * is the cheapest "this is alive" signal in the whole module.
 *
 * owner: characters
 */
import * as THREE from 'three';

const _v = new THREE.Vector3();
const _w = new THREE.Vector3();
const _r = new THREE.Vector3();
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();
const _qc = new THREE.Quaternion();
const _bx = new THREE.Vector3();
const _by = new THREE.Vector3();
const _bz = new THREE.Vector3();
const _m = new THREE.Matrix4();
const UP = new THREE.Vector3(0, 1, 0);
const FWD = new THREE.Vector3(0, 0, 1);

function basis(dir, ref, out) {
  _by.copy(dir).normalize();
  _bx.crossVectors(_by, ref);
  if (_bx.lengthSq() < 1e-10) _bx.crossVectors(_by, Math.abs(_by.z) < 0.9 ? FWD : UP);
  if (_bx.lengthSq() < 1e-10) _bx.set(1, 0, 0);
  _bx.normalize();
  _bz.crossVectors(_bx, _by);
  _m.makeBasis(_bx, _by, _bz);
  return out.setFromRotationMatrix(_m);
}

const CHAIN_DEFS = [
  { key: 'antenna', names: ['antennaA', 'antennaB', 'antennaC'], tip: [0.010, 0.150, -0.018], stiff: 0.055, damp: 0.955, grav: -1.1, maxDev: 0.26 },
  { key: 'shemagh', names: ['shemaghA', 'shemaghB'], tip: [0, -0.070, -0.024], stiff: 0.16, damp: 0.90, grav: -4.5, maxDev: 0.11 },
  { key: 'holster', names: ['holsterA'], tip: [0, -0.110, 0.004], stiff: 0.30, damp: 0.88, grav: -7.0, maxDev: 0.055 },
  { key: 'belt', names: ['beltA'], tip: [0, -0.095, 0.010], stiff: 0.28, damp: 0.88, grav: -7.0, maxDev: 0.060 },
];

class Chain {
  constructor(rig, def) {
    this.def = def;
    this.rig = rig;
    this.bones = def.names.map((n) => rig.byName.get(n));
    this.n = this.bones.length;
    // bind direction from bone i to bone i+1 (or the synthetic tip), in bind space
    this.dirs = [];
    this.lens = [];
    for (let i = 0; i < this.n; i++) {
      const a = rig.bind[def.names[i]];
      const b = i < this.n - 1
        ? rig.bind[def.names[i + 1]].clone()
        : a.clone().add(new THREE.Vector3(def.tip[0], def.tip[1], def.tip[2]).multiplyScalar(rig.scale));
      const d = b.clone().sub(a);
      this.lens.push(d.length());
      this.dirs.push(d.normalize());
    }
    this.p = [];
    this.prev = [];
    this.rest = [];
    for (let i = 0; i < this.n; i++) {
      this.p.push(new THREE.Vector3());
      this.prev.push(new THREE.Vector3());
      this.rest.push(new THREE.Vector3());
    }
    this.ready = false;
  }

  /** Rest positions of every particle for the CURRENT body pose (chain at identity). */
  _readRest() {
    for (const b of this.bones) b.quaternion.identity();
    this.bones[0].updateMatrixWorld(true);
    for (let i = 0; i < this.n; i++) {
      if (i < this.n - 1) this.bones[i + 1].getWorldPosition(this.rest[i]);
      else {
        const last = this.bones[this.n - 1];
        last.getWorldPosition(_v);
        last.getWorldQuaternion(_qa);
        this.rest[i].copy(this.dirs[i]).multiplyScalar(this.lens[i]).applyQuaternion(_qa).add(_v);
      }
    }
  }

  step(dt, rootQ) {
    const D = this.def;
    this._readRest();
    if (!this.ready) {
      for (let i = 0; i < this.n; i++) { this.p[i].copy(this.rest[i]); this.prev[i].copy(this.rest[i]); }
      this.ready = true;
    }

    const damp = Math.pow(D.damp, dt * 60);
    for (let i = 0; i < this.n; i++) {
      _v.copy(this.p[i]).sub(this.prev[i]).multiplyScalar(damp);
      this.prev[i].copy(this.p[i]);
      this.p[i].add(_v);
      this.p[i].y += D.grav * dt * dt;
    }

    // constraints: chain length from the (rigid) attach point, spring toward rest
    for (let it = 0; it < 3; it++) {
      for (let i = 0; i < this.n; i++) {
        const anchor = i === 0 ? this.bones[0].getWorldPosition(_w) : this.p[i - 1];
        _v.copy(this.p[i]).sub(anchor);
        const len = _v.length() || 1e-6;
        this.p[i].copy(anchor).addScaledVector(_v, this.lens[i] / len);
        // spring back toward the rest pose, and hard-clamp the deviation
        this.p[i].lerp(this.rest[i], D.stiff);
        _r.copy(this.p[i]).sub(this.rest[i]);
        const dev = _r.length();
        if (dev > D.maxDev * this.rig.scale) {
          this.p[i].copy(this.rest[i]).addScaledVector(_r, (D.maxDev * this.rig.scale) / dev);
        }
      }
    }

    // write rotations, sequentially so each bone sees its parent's new transform
    for (let i = 0; i < this.n; i++) {
      const bone = this.bones[i];
      bone.getWorldPosition(_v);
      _w.copy(this.p[i]).sub(_v);
      if (_w.lengthSq() < 1e-10) continue;
      _r.copy(this.dirs[i]).applyQuaternion(rootQ);
      _v.copy(FWD).applyQuaternion(rootQ);
      basis(_r, _v, _qa);
      basis(_w, _v, _qb);
      _qb.multiply(_qa.invert()).multiply(rootQ);
      bone.parent.getWorldQuaternion(_qc);
      bone.quaternion.copy(_qc.invert()).multiply(_qb);
      bone.updateMatrixWorld(true);
    }
  }
}

export class Secondary {
  constructor(rig, loadout) {
    this.rig = rig;
    this.chains = [];
    const want = {
      antenna: !!loadout.antenna,
      shemagh: !!loadout.shemagh,
      holster: !!loadout.holster,
      belt: !!loadout.dumpPouch,
    };
    for (const def of CHAIN_DEFS) if (want[def.key]) this.chains.push(new Chain(rig, def));
    this._rootQ = new THREE.Quaternion();
  }

  update(dt, root) {
    if (!this.chains.length || !(dt > 0)) return;
    const h = Math.min(dt, 1 / 30);
    root.getWorldQuaternion(this._rootQ);
    for (const c of this.chains) c.step(h, this._rootQ);
  }

  reset() { for (const c of this.chains) c.ready = false; }
}
